import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';

/** Number of digits in an ABA routing number. */
export const ROUTING_NUMBER_LENGTH = 9;

/** Positional weights of the ABA check-digit algorithm, repeating 3-7-1 across the nine digits. */
const ROUTING_NUMBER_CHECKSUM_WEIGHTS = [3, 7, 1, 3, 7, 1, 3, 7, 1];

/**
 * Whether a value is a plausible ABA routing number: nine digits whose weighted sum is a multiple
 * of ten, and not the all-zeros value (which satisfies the arithmetic but belongs to no bank).
 *
 * Deliberately NOT checked: the leading two digits (the Federal Reserve routing symbol) fall in
 * defined ranges, but a too-strict prefix table would reject a legitimate number and block a real
 * bank link — worse than the round-trip this check removes.
 */
export function isValidRoutingNumber(value: string): boolean {
    if (!new RegExp(`^\\d{${ROUTING_NUMBER_LENGTH}}$`).test(value)) {
        return false;
    }

    if (value === '0'.repeat(ROUTING_NUMBER_LENGTH)) {
        return false;
    }

    const sum = ROUTING_NUMBER_CHECKSUM_WEIGHTS.reduce(
        (total, weight, position) => total + (parseInt(value[position], 10) * weight),
        0,
    );

    return sum % 10 === 0;
}

/**
 * Reject a routing number the bank system cannot own, at the field, before submit (SD-9205).
 *
 * Length validators only prove the SHAPE of the value, so a made-up nine-digit number used to reach
 * Stripe and come back as a 400 "Invalid routing number" the user had to wait for — and an
 * error-level Sentry event for what is really a typo (Sentry CORE-1BX). The same check runs
 * server-side in core's `App\Rules\ValidAbaRoutingNumber`; this one is what the user actually sees.
 *
 * Empty values are left to `Validators.required`, and wrong-length values to
 * `Validators.minLength`/`maxLength`, so one mistake produces one message.
 */
export function routingNumberValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
        const value = control.value;

        if (value === null || value === undefined || value === '') {
            return null;
        }

        const asString = value.toString();

        // AI review: stay silent unless the value is the right LENGTH, so a half-typed number shows
        // one message instead of two. ControlMessagesComponent renders EVERY error on the control,
        // joined with <br />, so firing here as well as minLength made `12345` say both "This field
        // is not long enough." and "Please enter a valid 9-digit bank routing number.".
        //
        // This also realigns the validator with its server counterpart, App\Rules\ValidAbaRoutingNumber,
        // which returns early unless the value matches ^\d{9}$ precisely so the `digits:9` rule owns
        // the shape error. Nothing is lost: a nine-character value that is not nine DIGITS
        // (e.g. '06311403a') still falls through to the check below and is rejected there.
        if (asString.length !== ROUTING_NUMBER_LENGTH) {
            return null;
        }

        return isValidRoutingNumber(asString) ? null : { invalidRoutingNumber: true };
    };
}
